/**
 * Versión de la aplicación y novedades.
 *
 * REGLA: cada vez que se publique un cambio hay que subir APP_VERSION y agregar
 * una entrada nueva al inicio de CHANGELOG.
 *
 * El BUILD_ID se genera solo en cada compilación (no hay que tocarlo) y sirve
 * para detectar automáticamente si el cliente tiene una versión vieja abierta.
 */

declare const __BUILD_ID__: string;
declare const __BUILD_DATE__: string;

export const APP_VERSION = '1.13.0';

export const BUILD_ID: string =
  typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';

export const BUILD_DATE: string =
  typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : new Date().toISOString();

export interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '1.13.1',
    date: '2026-09-23',
    changes: [
      'El Super Admin ya no crea por error un menú propio al abrir "Menús QR": vuelve al Centro de Control.',
    ],
  },
  {
    version: '1.13.0',
    date: '2026-09-23',
    changes: [
      'La portada explica mejor que basta con subir la carta de tu restaurante para tener tu menú digital con QR.',
      'Un correo que ya tenía cuenta ya no se cuenta como registro nuevo.',
      'Embudo del administrador completo: visitas, prueba, registro, menú, publicación, precios, pago iniciado y pagado, con porcentajes reales.',
    ],
  },
  {
    version: '1.12.0',
    date: '2026-09-23',
    changes: [
      'Tu carta publicada aparece mejor en Google: título, descripción y ficha del negocio con tus datos reales.',
      'Una sola dirección por carta, aunque el enlace llegue con mayúsculas o con parámetros de campaña.',
      'Las pantallas privadas (panel, administración, cuenta) quedan fuera de los buscadores.',
      'Los datos de fuentes de tráfico ya sólo son visibles para administradores.',
    ],
  },
  {
    version: '1.11.0',
    date: '2026-09-23',
    changes: [
      'Pedidos por WhatsApp: tus clientes arman su pedido desde la carta y te lo mandan por WhatsApp.',
      'Los platillos sin precio aparecen como "Precio por confirmar" y no se suman al total.',
      'Cantidad máxima de 99 por platillo, sin cantidades en cero ni negativas.',
      'Si no activas pedidos, tu carta se ve y funciona exactamente igual que antes.',
    ],
  },
  {
    version: '1.10.0',
    date: '2026-09-23',
    changes: [
      'Ahora puedes entrar o crear tu cuenta con Google, sin escribir contraseña.',
      'El correo y la contraseña siguen funcionando igual, junto con la recuperación de contraseña.',
      'Si entras con Google por primera vez, llegas a la misma bienvenida guiada que un alta normal.',
      'Si venías de probar tu carta o de elegir un plan, no pierdes ese paso al entrar con Google.',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-09-23',
    changes: [
      'Al volver del pago, Plaxter dice "Confirmando tu pago…" y sólo activa Pro cuando el cobro está confirmado de verdad.',
      'Si la confirmación tarda, se avisa con claridad y se pide no pagar dos veces; nunca se queda cargando para siempre.',
      'Un doble clic en "Suscribirme" ya no abre dos pagos distintos.',
      'Si cancelas el pago, tu cuenta sigue igual y puedes intentarlo de nuevo cuando quieras.',
      'El aviso de plan activado aparece una sola vez, aunque tengas varias pestañas abiertas.',
    ],
  },
  {

    version: '1.8.0',
    date: '2026-09-23',
    changes: [
      'Cuando tu carta ya tiene un platillo real, el panel te recibe con tu avance: "Tu menú ya está tomando forma" o "Tu menú está listo para compartir".',
      'Una sola acción principal a la vez: "Publicar mi menú" si está oculto, o "Ver como cliente" y "Compartir mi QR" si ya está publicado. Nada se publica solo.',
      'Cuando ya completaste lo importante, el bloque se reduce a una línea: se acabó el onboarding insistente.',
      'Nueva tarjeta "Haz más con Pro" con tres beneficios concretos y un solo botón, sólo después de que tu menú ya funciona.',
      'El avance de activación ahora se calcula con hechos reales y no vuelve a pedir lo que ya hiciste.',
      'La invitación a Pro aparece discreta y sólo después de que tu carta ya tiene valor.',
      'Tu logo cuenta como personalizado sólo cuando subes el tuyo, no el de ejemplo.',
      'Nueva medición del momento de éxito, sin datos personales.',
    ],
  },
  {
    version: '1.7.3',
    date: '2026-09-23',
    changes: [
      'Corrección interna: al importar tu carta de prueba, el avance se guarda antes de revisar el contenido, para que una conexión lenta nunca deje la bienvenida a medias.',
    ],
  },
  {
    version: '1.7.2',
    date: '2026-09-23',
    changes: [
      'Al importar tu carta de prueba desde la bienvenida, ya no regresas a "¿Cómo quieres empezar?": avanza directo a revisar tu carta.',
      'La vista previa de la carta pendiente ya no se repite al moverse por la pantalla.',
    ],
  },
  {
    version: '1.7.1',
    date: '2026-09-23',
    changes: [
      'El menú sólo se da por listo cuando hay al menos un platillo real; una sección vacía ya no cuenta.',
      'La carta subida en la prueba se recupera dentro de la bienvenida: ya no se pide subirla otra vez.',
      'Se mide la creación de la primera sección propia.',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-09-23',
    changes: [
      'La portada móvil ahora empieza con "prueba tu propia carta": subes foto o PDF antes de crear cuenta.',
      'La prueba sin cuenta explica los errores, permite reintentar y ofrece continuar creando la cuenta.',
      'El registro avisa cuando hay una carta guardada esperando.',
      'La bienvenida detecta sola cuando ya tienes platillos: se acabó el paso que había que cerrar a mano.',
      'La pantalla final sólo dice que el menú está listo cuando de verdad tiene contenido.',
      'Nuevas mediciones del embudo: apertura del editor, primera sección y fallos de carga.',
    ],
  },
  {
    version: '1.6.3',
    date: '2026-09-23',
    changes: [
      'Ahora se registra cuándo un visitante ve realmente la sección de planes en la portada.',
      'La ventana de planes del panel cuenta igual que la de suscripción, sin conteos repetidos.',
      'El origen de campaña de cada visita se conserva mejor entre el anuncio y el registro.',
    ],
  },
  {
    version: '1.6.2',
    date: '2026-09-22',
    changes: [
      '“Administrar clientes” ahora aparece como acceso destacado, acción rápida y opción directa del selector superior.',
      'El buscador identifica claramente qué clientes tienen asistencia activa y cuáles necesitan activarla.',
      'La ruta visual muestra siempre Centro de Control, Administrar clientes y el restaurante abierto.',
    ],
  },
  {
    version: '1.6.1',
    date: '2026-09-22',
    changes: [
      '“Administrar clientes” ahora es una acción principal del Centro de Control y abre un buscador dedicado.',
      'La gestión de cuentas y contraseñas anteriores queda separada y señalada como función legado dentro de Usuarios.',
      'Al salir de la edición de un cliente se regresa al buscador de asistencia.',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-09-23',
    changes: [
      'Nuevo: administración delegada desde Super Admin para ayudar a clientes que lo piden, sin conocer ni cambiar su contraseña.',
      'Cada cambio hecho en nombre de un cliente queda registrado con quién lo hizo y qué modificó.',
      'La ayuda se activa y se desactiva por cuenta, con confirmación previa.',
      'El logotipo del cliente ahora se sube como archivo desde la pantalla de ayuda, sin pegar enlaces.',
    ],
  },
  {
    version: '1.5.10',
    date: '2026-09-22',
    changes: [
      'Corrección urgente: las cartas públicas volvieron a cargar (la medición de primera visita impedía mostrar el menú).',
      'La medición de primera visita queda aislada: nunca puede afectar la carga de una carta.',
    ],
  },
  {
    version: '1.5.9',
    date: '2026-09-22',
    changes: [
      'El aviso de "llegaste al tope de tu plan" ahora se reconoce con exactitud y se registra una sola vez.',
      'Las cuentas que ya tienen más contenido del que su plan permite conservan todo su menú.',
      'Se registra la primera visita real a una carta publicada, sin guardar ningún dato del visitante.',
      'El Centro de Control muestra el ciclo de vida de las cuentas con definiciones claras y sin cifras inventadas.',
      'Sin cambios de precios, planes ni funciones gratuitas. No se envió ningún correo.',
    ],
  },
  {
    version: '1.5.8',
    date: '2026-09-22',
    changes: [
      'Aviso amable al llegar a 8 de 10 platillos o 2 de 3 categorías: informa, no bloquea.',
      'La analítica y las promociones bloqueadas ahora explican mejor qué incluye Pro, con un ejemplo ilustrativo marcado como tal.',
      'El Centro de Control muestra conteos reales de altas, primer platillo, cartas publicadas e interés en Pro.',
      'Ningún cambio de precios, límites ni funciones gratuitas: publicar, QR, importar carta, pedidos y PDF siguen incluidos.',
    ],
  },
  {
    version: '1.5.7',
    date: '2026-09-22',
    changes: [
      'Nueva vista de Monetización en el Centro de Control con los conteos reales de cuentas, cartas y pagos.',
      'Catálogo único de funciones: publicar, QR, importar carta, pedidos por WhatsApp y PDF siguen incluidos gratis.',
      'El dominio propio deja de presentarse como beneficio: todavía no existe.',
      'Sin cambios de precios ni de límites: Gratis 10 platillos / 3 categorías y Pro 200 / 20.',
    ],
  },
  {
    version: '1.5.6',
    date: '2026-09-22',
    changes: [
      'La carta que subes antes de crear tu cuenta ahora se puede importar desde tu panel, oculta hasta que la revises.',
      '"Guardar y agregar otro" mantiene abierto el formulario de platillos.',
      'El perfil del empleado ya no borra su dirección, contactos de emergencia ni referencias.',
      'Las preguntas frecuentes de menú gratis muestran el precio correcto del plan Pro.',
    ],
  },
  {
    version: '1.5.5',
    date: '2026-09-22',
    changes: [
      'Nuevo conmutador de áreas: siempre hay camino entre Menús QR, Centro de Control y Recursos Humanos.',
      'Vista demo del Super Admin: ver la interfaz como plan Gratis o Plaxter Menu Pro, sólo visual y de sólo lectura.',
      'La vista demo se avisa con una banda permanente y se sale en un clic; no altera cuentas, cobros ni permisos.',
    ],
  },
  {
    version: '1.5.4',
    date: '2026-09-22',
    changes: [
      'Las métricas separan las altas totales de Plaxter (no atribuidas) de la conversión real de la landing.',
      'Se eliminó el país inventado "México 100%"; la ubicación se muestra como aproximada.',
      'Las recomendaciones de inversión sólo aparecen con muestra suficiente (100 visitas y 10 conversiones por canal).',
    ],
  },
  {

    version: '1.5.3',
    date: '2026-09-22',
    changes: [
      'El aviso de "Nueva versión disponible" ahora se aplica de un solo clic, recarga una sola vez y ya no reaparece.',
      'Se puede posponer la actualización 24 horas; una versión posterior vuelve a avisar.',
      'Super Admin entra directamente al Centro de Control.',
    ],
  },
  {
    version: '1.5.2',
    date: '2026-09-21',
    changes: [
      'El logotipo de Plaxter ahora usa su tipografía oficial en todas las pantallas de marca.',
    ],
  },
  {
    version: '1.5.1',
    date: '2026-09-12',
    changes: [
      'Menú de demostración sin fotos externas: ya no aparecen imágenes rotas.',
      'Se eliminó un aviso de error en la consola del navegador presente en todas las páginas.',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-09-12',
    changes: [
      'Página principal renovada: menú QR, prueba gratis y precio Pro claros, con demo real.',
      'Guía de activación en el panel: platillos con precio, marca propia y código QR listo para compartir.',
      'Panel de administración con salud de cuentas y clientes gestionados.',
      'Mejoras de seguridad y privacidad en el registro de visitas de los menús públicos.',
    ],
  },
  {
    version: '1.4.2',
    date: '2026-08-25',
    changes: [
      'Asistencia: los gerentes registran entrada y salida solo de su sucursal, con mensajes claros si el empleado es de otra.',
      'La lista de asistencia se actualiza al instante tras cada registro y tiene botón "Actualizar".',
      'Se eliminó una ficha de empleada duplicada que hacía parecer que el registro no funcionaba.',
    ],
  },
  {
    version: '1.4.1',
    date: '2026-08-13',
    changes: [
      'Se corrigió la carga de fotografías al crear empleados nuevos.',
      'Las fotos continúan privadas y separadas por empresa y empleado.',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-08-13',
    changes: [
      'Se corrigió el error al crear, aprobar o pagar periodos de nómina.',
      'Control de pago por empleado: pendiente, parcial o pagado, con fecha, método y nota.',
      'Botón para marcar pagado todo el periodo de una sola vez y revertir pagos por error.',
      'Panel de nómina con costo del periodo, pagado, pendiente y comparativo contra el periodo anterior.',
      'Gráfica de los últimos 6 periodos (solo director y super administrador).',
      'Cada empleado ve en su portal si ya se le pagó, cuánto y en qué fecha.',
      'Los gerentes solo ven y pagan la nómina de su sucursal.',
    ],
  },
  {

    version: '1.3.0',
    date: '2026-08-13',
    changes: [
      'Nuevo pase de lista con código QR: la sucursal deja una pantalla fija (Kiosco) con un QR que cambia cada 30 segundos.',
      'Cada empleado escanea con su propio celular; la hora la registra el servidor, así nadie puede pasar lista por otro.',
      'La asistencia indica si el registro fue por QR o capturado a mano.',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-08-11',
    changes: [
      'El número de versión ahora cambia con cada publicación.',
      'El botón "Buscar actualizaciones" detecta de verdad si hay una versión nueva y la instala al instante.',
      'Aviso automático en pantalla cuando se publica una mejora mientras la app está abierta.',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-08-11',
    changes: [
      'Las fotos y evidencias se suben desde la cámara o la galería, sin pegar enlaces.',
      'Expediente del empleado con dirección, contactos de emergencia y referencias personales.',
      'Cada empleado puede actualizar su foto y sus datos de contacto desde su portal.',
      'Sueldo por semana, quincena o mes con conversión automática en nómina.',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-08-07',
    changes: [
      'Panel reorganizado por orden de uso: día a día, cierre de periodo y configuración.',
      'La configuración inicial desaparece sola al terminarse.',
      'Guía numerada de operación en la pantalla de Inicio.',
      'Versión visible y botón para buscar actualizaciones.',
    ],
  },
];

export const DEVELOPER = {
  name: 'Castelan Interactive Inc.',
  url: 'https://castelan.app/',
} as const;
